/**
 * MessageReactionButton Type Definitions
 * @layer atom
 * @package @plerity/ui-core
 */

import type { ReactNode } from "react";

/** Active tint — `like` renders blue, `done` renders emerald. */
export type ReactionTone = "like" | "done";

/** Canonical emoji for the built-in "like" reaction. */
export const LIKE_EMOJI = "👍";
/** Canonical emoji for the built-in "done" reaction. */
export const DONE_EMOJI = "✅";

export interface MessageReactionButtonProps {
  /** The reaction emoji this button toggles (e.g. `LIKE_EMOJI`, `DONE_EMOJI`). */
  emoji: string;
  /** Whether the viewer has already applied this reaction. */
  active: boolean;
  /**
   * Fired on click. `active` is the CURRENT state at click time — the caller
   * is responsible for adding the reaction when `active` is `false` and
   * removing it when `active` is `true`.
   */
  onToggle: (emoji: string, active: boolean) => void;
  /** `aria-label` (and `title`) shown when `active` is `false` — the "add" action. */
  labelInactive: string;
  /** `aria-label` (and `title`) shown when `active` is `true` — the "remove" action. */
  labelActive: string;
  /** Active tint — `like` → blue ramp, `done` → emerald ramp. Defaults to `"like"`. */
  tone?: ReactionTone;
  /** Override the built-in inline-SVG icon. */
  icon?: ReactNode;
  /**
   * When `false` (default) the button hover/focus-reveals via opacity — it
   * relies on an ancestor with the `group` class. When `active` the button
   * always stays visible regardless of this prop.
   */
  alwaysVisible?: boolean;
  /** Disables the button. */
  disabled?: boolean;
  /** Merged onto the root `<button>` via `cn`. */
  className?: string;
  /** Rendered as `data-testid`. */
  testId?: string;
}

export interface MessageReactionQuickActionsProps {
  /** Whether the viewer has already liked this message. */
  liked: boolean;
  /** Whether the viewer has already marked this message done. */
  done: boolean;
  /** Fired for either button; forwarded verbatim from `MessageReactionButton.onToggle`. */
  onToggle: (emoji: string, active: boolean) => void;
  /** Message author's display name — used to compose the aria-labels. */
  senderName: string;
  /** Used to build stable `data-testid`s (`message-like-{id}` / `message-done-{id}`). */
  messageId: number | string;
  /** Forwarded to both buttons. See {@link MessageReactionButtonProps.alwaysVisible}. */
  alwaysVisible?: boolean;
  /** Forwarded to both buttons. */
  disabled?: boolean;
  /** Merged onto the wrapping `<span>` via `cn`. */
  className?: string;
}
